import { redactCredentials } from '@casa/bridge';

// The page's log (plan step 1.11): the last entries in a ring buffer, for the settings page and the
// remote sink. Everything is masked as it is written: credentials the bridge recognises (URLs with
// a user part, authorization values, secret-looking query parameters) and the values the dashboard
// marks as credentials (decision §12), which only the app knows.

export type LogLevel = 'info' | 'warn' | 'error';

export interface LogEntry {
    /** Increases by one per entry, across the whole run; the sink sends from the last one it sent. */
    readonly seq: number;
    /** Milliseconds since the Unix epoch. */
    readonly time: number;
    readonly level: LogLevel;
    readonly message: string;
}

export interface Log {
    write(level: LogLevel, message: string) : void;
    /** The kept entries, oldest first. */
    entries() : readonly LogEntry[];
    subscribe(listener: () => void) : () => void;
    /** Values masked wherever they appear from now on: the dashboard's credentials. */
    setSecrets(values: Iterable<string>) : void;
}

export const DEFAULT_LOG_CAPACITY = 1000;
const MASK = '***';
/** Shorter values would mask ordinary words and numbers. */
const MIN_SECRET_LENGTH = 4;

/** `message` with credentials masked: those the bridge recognises, and each of `secrets`. */
export function redact(message: string, secrets: readonly string[]) : string {
    let text = redactCredentials(message);
    for (const secret of secrets) {
        text = text.split(secret).join(MASK);
    }

    return text;
}

export function createLog(capacity = DEFAULT_LOG_CAPACITY) : Log {
    const kept: LogEntry[] = [];
    const listeners = new Set<() => void>();
    let secrets: string[] = [];
    let nextSeq = 1;

    return {
        write(level, message) {
            kept.push({ seq: nextSeq++, time: Date.now(), level, message: redact(message, secrets) });
            if (kept.length > capacity) {
                kept.splice(0, kept.length - capacity);
            }
            for (const listener of [...listeners]) {
                listener();
            }
        },
        entries: () => kept,
        subscribe(listener) {
            listeners.add(listener);

            return () => listeners.delete(listener);
        },
        setSecrets(values) {
            // Longest first, so that a secret containing another is masked whole.
            secrets = [...new Set(values)].filter((value) => value.length >= MIN_SECRET_LENGTH).sort((a, b) => b.length - a.length);
        },
    };
}

function describe(value: unknown) : string {
    if (typeof value === 'string') {
        return value;
    }
    if (value instanceof Error) {
        return `${value.name}: ${value.message}`;
    }
    try {
        return JSON.stringify(value) ?? String(value);
    } catch {
        return String(value);
    }
}

export interface ConsoleLike {
    info(...args: unknown[]) : void;
    warn(...args: unknown[]) : void;
    error(...args: unknown[]) : void;
}

/**
 * Copies what the page writes to the console into `log`; the console still shows it. Every part of
 * the app, packages included, already reports through the console.
 */
export function captureConsole(log: Log, target: ConsoleLike) : void {
    for (const level of ['info', 'warn', 'error'] as const) {
        const original = target[level].bind(target);
        target[level] = (...args: unknown[]) => {
            original(...args);
            log.write(level, args.map(describe).join(' '));
        };
    }
}

/** The log of this page. */
export const appLog = createLog();
